import { getRootUrl } from '@shared-config/rootUrl';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAtom } from 'jotai';

import { getVendorListAPI } from 'src/api';
import {
  BlockedRetailerModal,
  CartItem,
  ConfirmActionModal,
  EmbedModal,
  ListActions,
  LoadingCart,
  SimpleSpinner,
  ToastStyled,
} from 'src/components';
import { showEmbedModalAtom } from 'src/store';
import { useRedirectToUpperCase } from 'src/hooks';
import { useRemoveCartFromUserOrCookie } from 'src/hooks/useRemoveCartFromUserOrCookie';
import { useSaveCartToUserOrCookie } from 'src/hooks/useSaveCartToUserOrCookie';
import type { CartGetData, CartGetItem } from 'src/types';
import { exportCartToExcel, isAmazonLocalCart, localUrl } from 'src/utils';
import { formatPrice } from 'src/utils/formatPrice';

import { MakeChat } from './chat/MakeChat';
import { CartNotice } from './components/CartNotice';
import { RegionSelect } from './components/RegionSelect';
import { SaveSuccessModal } from './components/SaveSuccessModal';
import { VendorSelect } from './components/VendorSelect';
import { AMAZON_REGIONS, LOCATION_SEARCH_SUPPORTED } from './constants';
import {
  cartGetItemToMakeItem,
  makeItemToCartGetItem,
  saveCart,
  searchItems,
  searchResultToMakeItem,
  toShareUrlPath,
  type MakeItem,
} from './makeApi';
import { normalizePriceToNumber } from './priceUtils';

const errorToast = (msg: string) =>
  toast.custom((id) => (
    <ToastStyled variant='error' handleClose={() => toast.dismiss(id)}>
      {msg}
    </ToastStyled>
  ));

const validZip = (zip: string): string | undefined =>
  /^(\d{5})$|^(\d{5}-\d{4})$/.test(zip) ? zip : undefined;

const toggle = (set: Set<number>, i: number): Set<number> => {
  const next = new Set(set);
  if (next.has(i)) next.delete(i);
  else next.add(i);
  return next;
};

// Merge items into the cart: add the incoming quantity when the same asin+sku
// is already present, otherwise append with the item's chosen quantity.
const mergeItems = (cart: MakeItem[], toAdd: MakeItem[]): MakeItem[] => {
  const next = [...cart];
  for (const item of toAdd) {
    const qty = item.quantity || 1;
    const i = next.findIndex(
      (p) => p.asin === item.asin && !!item.sku && !!p.sku && item.sku === p.sku,
    );
    if (i >= 0) next[i] = { ...next[i], quantity: next[i].quantity + qty };
    else next.push({ ...item, quantity: qty });
  }
  return next;
};

export const Make = () => {
  const { t } = useTranslation();
  const { cartId: cartIdParam } = useParams();
  const [searchParams] = useSearchParams();

  const { data: vendors } = useQuery({
    queryKey: ['vendor-list'],
    queryFn: getVendorListAPI,
    staleTime: 1000 * 60 * 60,
  });

  const saveCartToUserOrCookie = useSaveCartToUserOrCookie();
  const removeCartFromUserOrCookie = useRemoveCartFromUserOrCookie();
  const navigate = useNavigate();

  // Normalized (uppercased) cart id — the edit query waits for it so a
  // lowercase URL never triggers a fetch before the redirect.
  const [cartId, setCartId] = useState('');
  useRedirectToUpperCase({ cartIdParam, setCartId, pathname: 'make' });

  // ----- Cart-being-edited (when /make/:id) -----
  const editQuery = useQuery({
    queryKey: ['make-edit-cart', cartId],
    enabled: !!cartId,
    queryFn: async (): Promise<CartGetData> => {
      const res = await fetch(`/api/get/r/cart/${cartId}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Failed to load cart');
      const data = await res.json();
      if (data.error) throw new Error('Cart not found');
      return data as CartGetData;
    },
  });

  // ----- State -----
  const [vendorId, setVendorId] = useState('walmart');
  const [region, setRegion] = useState('us');
  const [zip, setZip] = useState('');
  const [keywords, setKeywords] = useState('');

  const [searchResults, setSearchResults] = useState<MakeItem[]>([]);
  const [selectedSearch, setSelectedSearch] = useState<Set<number>>(new Set());
  const [searching, setSearching] = useState(false);
  // True once a search has started; hides the "search to get started"
  // placeholder even when the search errors or returns nothing.
  const [searchActive, setSearchActive] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchLabel, setSearchLabel] = useState<string | null>(null);

  const [items, setItems] = useState<MakeItem[]>([]);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState('');
  const [password, setPassword] = useState('');
  const [note, setNote] = useState('');

  const [savedCartId, setSavedCartId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [vendorLocked, setVendorLocked] = useState(false);

  const [showSuccess, setShowSuccess] = useState(false);
  const [showEmbed, setShowEmbed] = useAtom(showEmbedModalAtom);
  const [pendingDelete, setPendingDelete] = useState<number | 'bulk' | null>(null);

  const isEdit = !!cartIdParam;
  // While the uppercase redirect is pending, the query is disabled and
  // isLoading is false — still treat it as loading to avoid UI flicker.
  const editLoading = isEdit && (editQuery.isLoading || !cartId);

  // ----- Hydrate from an existing cart (edit mode) -----
  useEffect(() => {
    const c = editQuery.data;
    if (!c) return;
    setVendorId(c.vendor);
    setVendorLocked(true);
    setRegion(c.locale || 'us');
    setItems((c.items || []).map(cartGetItemToMakeItem));
    setTitle(c.title || '');
    setNote(c.note || '');
    setSavedCartId(c.id);
  }, [editQuery.data]);

  // ----- Failed to load the cart to edit — return to a clean /make -----
  useEffect(() => {
    if (editQuery.isError) navigate('/make', { replace: true });
  }, [editQuery.isError, navigate]);

  // ----- New-cart URL params (?vendor, ?keywords) -----
  const initRan = useRef(false);
  useEffect(() => {
    if (isEdit || !vendors || initRan.current) return;
    initRan.current = true;
    const vParam = searchParams.get('vendor');
    const kwParam = searchParams.get('keywords');
    if (vParam) setVendorId(vParam);
    if (kwParam) {
      setKeywords(kwParam);
      void runSearch(vParam || vendorId, kwParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendors, isEdit]);

  // ----- Warn on unsaved changes -----
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // ----- Removing the last item from a saved cart deletes it & returns to /make -----
  useEffect(() => {
    if (!savedCartId || items.length > 0) return;
    removeCartFromUserOrCookie(savedCartId);
    setSavedCartId(null);
    setTitle('');
    setNote('');
    setPassword('');
    setVendorLocked(false);
    setDirty(false);
    navigate('/make');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.length, savedCartId]);

  // ----- Derived vendor / locale / totals -----
  const vendorMeta = vendors?.find((v) => v.vendorId === vendorId);
  const showRegion = vendorId === 'amazon';
  const showZip = LOCATION_SEARCH_SUPPORTED.includes(vendorId);

  const groceryDisabled = useMemo(() => {
    const c = editQuery.data;
    if (!c) return false;
    const store = c.store || '';
    if (c.vendor === 'amazon' && isAmazonLocalCart(store))
      return true;
    if (c.vendor === 'walmart' && store.includes('grocery')) return true;
    return false;
  }, [editQuery.data]);

  // Shopify is intentionally hidden from the retailer funnel (showVendor:false) but DOES
  // support token-free item search via search/suggest.json, so enable it explicitly.
  const searchEnabled = ((vendorMeta?.showVendor ?? false) || vendorId === 'shopify') && !groceryDisabled;

  const locale = editQuery.data?.locale || (vendorId === 'amazon' ? region : 'us');
  const ccyItem = items.find((i) => i.priceccy || i.ccy);
  const ccySItem = items.find((i) => i.priceccyS || i.ccyS);
  const cartCcy = editQuery.data?.cartCCY || ccyItem?.priceccy || ccyItem?.ccy || 'USD';
  const cartCcyS = editQuery.data?.cartCCYS || ccySItem?.priceccyS || ccySItem?.ccyS || '$';

  // Search results may carry their own currency (e.g. amazon.de on an empty cart).
  const searchCcyItem = searchResults.find((i) => i.priceccy || i.ccy);
  const searchCcySItem = searchResults.find((i) => i.priceccyS || i.ccyS);
  const searchCcy = searchCcyItem?.priceccy || searchCcyItem?.ccy || cartCcy;
  const searchCcyS = searchCcySItem?.priceccyS || searchCcySItem?.ccyS || cartCcyS;

  const totalLabel = useMemo(() => {
    const total = items.reduce(
      (sum, it) => sum + normalizePriceToNumber(it.price, locale) * it.quantity,
      0,
    );
    return formatPrice(
      Number(total.toFixed(locale === 'jp' ? 0 : 2)),
      locale,
      cartCcy,
      cartCcyS,
      true,
    );
  }, [items, locale, cartCcy, cartCcyS]);

  // ----- Search -----
  const runSearch = async (
    vOverride?: string,
    kwOverride?: string,
    regionOverride?: string,
    zipOverride?: string,
  ): Promise<MakeItem[]> => {
    const v = vOverride ?? vendorId;
    const kw = (kwOverride ?? keywords).trim();
    if (!v) return [];
    if (!kw) {
      if (!kwOverride) setSearchError(t('make.your_search_cant_be_empty_please_type_something'));
      return [];
    }

    setSearchError(null);
    setSearching(true);
    setSearchActive(true);
    setSearchResults([]);
    setSelectedSearch(new Set());

    try {
      const { items: results, redirectedTo } = await searchItems(
        v,
        kw,
        v === 'amazon' ? regionOverride ?? region : undefined,
        LOCATION_SEARCH_SUPPORTED.includes(v) ? zipOverride ?? validZip(zip) : undefined,
        v === 'shopify' ? editQuery.data?.ap : undefined,
      );
      if (redirectedTo) {
        window.location.href = redirectedTo;
        return [];
      }
      if (results.length === 0) {
        setSearchError(t('make.no_items_found_for_this_query_please_try_again'));
        setSearchLabel(null);
        return [];
      }
      const mapped = results.map(searchResultToMakeItem).filter((i) => i.asin);
      if (mapped.length === 0) {
        setSearchError(t('make.no_items_found_for_this_query_please_try_again'));
        setSearchLabel(null);
        return [];
      }
      setSearchResults(mapped);
      setSearchLabel(kw);
      return mapped;
    } catch {
      setSearchError(t('make.no_items_found_for_this_query_please_try_again'));
      return [];
    } finally {
      setSearching(false);
    }
  };

  // The assistant searches and renders results inside the chat (not the page),
  // but still targets the page's real vendor + cart so Save works correctly.
  const handleChatSearch = async (
    vendor: string,
    keywords: string,
    region?: string,
    zip?: string,
  ): Promise<MakeItem[]> => {
    if (!vendorLocked) setVendorId(vendor);
    if (region) setRegion(region);
    const { items: results, redirectedTo } = await searchItems(
      vendor,
      keywords,
      vendor === 'amazon' ? region : undefined,
      LOCATION_SEARCH_SUPPORTED.includes(vendor) ? validZip(zip || '') : undefined,
      vendor === 'shopify' ? editQuery.data?.ap : undefined,
    );
    if (redirectedTo) return [];
    return results
      .map(searchResultToMakeItem)
      .filter((i) => i.asin)
      .slice(0, 10);
  };

  const handleClearSearch = () => {
    setSearchResults([]);
    setSelectedSearch(new Set());
    setSearchLabel(null);
    setSearchError(null);
    setSearchActive(false);
    setKeywords('');
  };

  const onVendorChange = (next: string) => {
    setVendorId(next);
    setSearchResults([]);
    setSelectedSearch(new Set());
    setSearchLabel(null);
    setSearchError(null);
    setSearchActive(false);
  };

  // ----- Cart mutations -----
  const addItem = (item: MakeItem) => setItems((prev) => mergeItems(prev, [item]));

  // Items chosen via the AI assistant land in the page's live cart.
  const addItemsFromChat = (toAdd: MakeItem[]) => {
    if (!toAdd.length) return;
    setItems((prev) => mergeItems(prev, toAdd));
    setDirty(true);
  };

  // Cart API exposed to the assistant's macro tools (remove / quantity / clear).
  const removeCartIndexes = (indexes: number[]) => {
    const drop = new Set(indexes);
    setItems((prev) => prev.filter((_, i) => !drop.has(i)));
    setDirty(true);
  };
  const setCartQuantity = (index: number, qty: number) => {
    const clamped = Math.max(1, Math.min(999, qty));
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, quantity: clamped } : it)));
    setDirty(true);
  };
  const clearCart = () => {
    setItems([]);
    setDirty(true);
  };

  const commitAdd = () => {
    setTitle((prev) => (prev.trim() ? prev : keywords.trim()));
    setSearchResults([]);
    setSelectedSearch(new Set());
    setSearchLabel(null);
    setSearchActive(false);
    setKeywords('');
    setDirty(true);
  };

  const handleAddOne = (index: number) => {
    addItem(searchResults[index]);
    commitAdd();
  };

  const handleAddSelected = () => {
    if (selectedSearch.size === 0) return;
    selectedSearch.forEach((idx) => addItem(searchResults[idx]));
    commitAdd();
  };

  // Quantity picked on a search result before it's added to the cart.
  const handleSearchQuantityChange = (index: number, quantity: number) => {
    setSearchResults((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
  };

  const handleQuantityChange = (index: number, quantity: number) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], quantity };
      return next;
    });
    setDirty(true);
  };

  const confirmDelete = () => {
    if (pendingDelete == null) return;
    if (pendingDelete === 'bulk') {
      setItems((prev) => prev.filter((_, i) => !selectedItems.has(i)));
      setSelectedItems(new Set());
    } else {
      const idx = pendingDelete;
      setItems((prev) => prev.filter((_, i) => i !== idx));
    }
    setPendingDelete(null);
    setDirty(true);
  };

  // ----- Save / actions -----
  const buildSaveArgs = (asNew: boolean) => {
    const dest =
      vendorId === 'amazon'
        ? AMAZON_REGIONS.find((r) => r.queryName === region)?.site
        : undefined;
    return {
      items,
      vendor: vendorId,
      cartId: asNew ? null : savedCartId,
      title,
      note,
      password,
      dest,
      ap: editQuery.data?.ap,
      ccy: items.find((i) => i.priceccy)?.priceccy,
    };
  };

  // The save endpoint reports a wrong edit-password as
  // "Persistance error: Password does not match".
  const saveErrorToast = (err: unknown) => {
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    errorToast(
      msg.includes('password')
        ? t('make.incorrect_password')
        : t('make.server_encountered_an_error'),
    );
    console.error(err);
  };

  const handleSave = async () => {
    if (items.length === 0) return;
    try {
      const newId = await saveCart(buildSaveArgs(false));
      setSavedCartId(newId);
      setDirty(false);
      saveCartToUserOrCookie(newId);
      setShowSuccess(true);
      // Reflect edit mode in the URL for brand-new carts — client-side, no reload.
      // The cart is already in state, so the background refetch causes no flash.
      if (!cartIdParam) navigate(`/make/${newId}`, { replace: true });
    } catch (err) {
      saveErrorToast(err);
    }
  };

  const handleDuplicate = async () => {
    try {
      const newId = await saveCart(buildSaveArgs(true));
      saveCartToUserOrCookie(newId);
      window.location.href = `/make/${newId}`;
    } catch (err) {
      saveErrorToast(err);
    }
  };

  const handleExport = () => {
    const rows = items.map(
      (i) =>
        ({
          asin: i.asin,
          title: i.title || '',
          quantity: i.quantity,
          price: i.price ?? '',
          ccyS: i.ccyS || i.priceccyS,
          priceccy: i.priceccy || i.ccy,
          sku: i.sku,
          skuString: i.skuString,
          url: toShareUrlPath(i.url),
        }) as CartGetItem,
    );
    exportCartToExcel(
      savedCartId ?? '',
      cartCcy,
      cartCcyS,
      rows,
      title || undefined,
      editQuery.data?.timestamp ?? Date.now(),
      editQuery.data?.store,
    );
  };

  const hasItems = items.length > 0;
  const canonical = `${getRootUrl()}/make${cartIdParam ? `/${cartIdParam}` : ''}`;

  // Heads-up when a meaningful share of items couldn't fetch product details.
  const missingDetailsCount = items.filter((i) => !i.title).length;
  const showMissingDetails =
    missingDetailsCount >= 2 && (missingDetailsCount / items.length) * 100 > 30;

  return (
    <>
      <Helmet>
        <title>{`Share-A-Cart · ${t('make.make_shopping_suggestions')}`}</title>
        <link rel='canonical' href={canonical} />
      </Helmet>

      {/* Hero */}
      <div className='p-6 bg-primary-dark text-white font-primary'>
        <div className='px-1 sm:px-6 pt-12 pb-16'>
          <h1 className='text-center text-4xl lg:text-5xl font-bold'>
            {isEdit ? t('make.edit_your_list') : t('make.create_a_list')}
          </h1>
        </div>
      </div>

      <main className='p-6 font-primary font-semibold text-primary-dark flex flex-col gap-y-2 sm:gap-y-4 lg:gap-y-14 xl:max-w-7xl xl:mx-auto'>
        <p className='w-full'>
          {t('make.share_with_friends_enemies_coworkers_search_to_get_started')}{' '}
          <a
            className='text-primary hover:text-primary/70 transition-all'
            href={localUrl('/guide/creating-a-list')}
          >
            {t('make.help_me')}
          </a>
        </p>

        <div className='flex flex-col items-center lg:items-start lg:flex-row gap-x-2 gap-y-2 sm:gap-x-4 sm:gap-y-4 lg:max-w-5xl'>
          <div className='grow-0 shrink-0 flex flex-row flex-wrap lg:flex-nowrap gap-x-2 sm:gap-x-4 gap-y-2 justify-start items-center'>
            <div className='flex items-center gap-x-2 sm:gap-x-4 shrink-0'>
              <p className='whitespace-wrap w-full max-w-min xs:max-w-fit'>
                {isEdit ? t('make.retailer') : t('common.select_retailer')}
              </p>
              <VendorSelect
                vendors={vendors || []}
                value={vendorId}
                onChange={onVendorChange}
                disabled={vendorLocked || hasItems}
              />
            </div>

            {showRegion && (
              <RegionSelect
                value={region}
                onChange={setRegion}
                disabled={vendorLocked || hasItems}
              />
            )}

            {showZip && (
              <input
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                placeholder={t('make.zip_code')}
                className='w-[120px] shrink-0 h-12 py-2 px-3 rounded-[4px] text-primary-dark text-base font-secondary font-normal border-2 border-gray-200 focus:border-primary hover:cursor-text hover:bg-cloudGray transition'
              />
            )}
          </div>

          <div className='grow flex flex-row flex-nowrap justify-center items-center rounded-md w-full max-w-lg'>
            <div className='flex flex-row flex-nowrap grow'>
              <button
                type='button'
                disabled={!searchEnabled}
                onClick={() => runSearch()}
                className='hidden xs:flex justify-center items-center border-r-0 border-2 border-primary rounded-l text-white bg-primary hover:bg-primary-hover hover:cursor-pointer transition h-12 disabled:opacity-50 disabled:pointer-events-none'
              >
                <i className='text-white material-icons-outlined py-2 px-2'>search</i>
              </button>
              <input
                className='w-full h-12 disabled:bg-gray-200 disabled:border-gray-200 disabled:cursor-text disabled:hover:bg-gray-200 border-2 border-cloudGray border-r-0 xs:border-x-0 xs:grow rounded-l xs:rounded-none text-primary-dark text-base font-secondary font-normal py-2 px-2 xs:px-3 focus:border-primary focus:outline-none focus:ring-0 hover:bg-cloudGray transition'
                type='text'
                value={keywords}
                disabled={!searchEnabled}
                placeholder={
                  searchEnabled
                    ? t('make.search_for_items')
                    : t('make.this_retailer_does_not_support_item_search')
                }
                onChange={(e) => setKeywords(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') runSearch();
                }}
              />
            </div>
            <button
              type='button'
              disabled={!searchEnabled}
              onClick={() => runSearch()}
              className='h-12 py-2 px-4 bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:pointer-events-none text-white rounded-r border-l-0 border-primary border-2 hover:cursor-pointer transition'
            >
              {t('common.search')}
            </button>
          </div>
        </div>

        {searchError && (
          <p className='font-secondary font-normal text-red-500'>{searchError}</p>
        )}
      </main>

      <div className='w-full h-0.5 bg-gray-200 mt-4' />

      {/* Search results */}
      {searchLabel && searchResults.length > 0 && (
        <div className='flex justify-between items-center max-h-[76px] h-[76px] p-4 font-secondary font-normal text-primary-dark'>
          <div className='flex items-center gap-6'>
            <p>
              {t('make.search_results_for')} “{searchLabel}”
            </p>
            <button
              type='button'
              onClick={handleClearSearch}
              className='button-secondary h-10 inline-flex items-center gap-1'
            >
              <i className='material-icons-outlined text-[18px] leading-none'>close</i>
              {t('make.clear_search')}
            </button>
          </div>
          {selectedSearch.size > 0 && (
            <button
              type='button'
              onClick={handleAddSelected}
              className='py-2 px-4 text-center text-primary font-bold border-2 border-primary rounded-full bg-white hover:bg-primary/20 transition'
            >
              {t('make.add_selected')} [{selectedSearch.size}]
            </button>
          )}
        </div>
      )}

      {searching && (
        <div className='mx-auto w-fit py-10'>
          <SimpleSpinner customWidth='w-12' customHeight='h-12' customPosition='block' />
        </div>
      )}

      {searchResults.length > 0 && (
        <ul className='flex w-full flex-col items-center justify-center py-2'>
          {searchResults.map((item, i) => (
            <CartItem
              key={`s-${i}-${item.asin}`}
              item={makeItemToCartGetItem(item, locale)}
              ccy={searchCcy}
              ccys={searchCcyS}
              vendor={vendorId}
              compare={false}
              locale={locale}
              page='make'
              selected={selectedSearch.has(i)}
              onToggleSelect={() => setSelectedSearch((s) => toggle(s, i))}
              onQuantityChange={(q) => handleSearchQuantityChange(i, q)}
              qtyErrorMessage={t('make.qty_must_be_any_num_1_999')}
              action={
                <button
                  type='button'
                  onClick={() => handleAddOne(i)}
                  className='w-full min-w-fit max-w-[280px] mx-auto flex justify-center items-center font-secondary font-bold text-center h-12 py-2 px-4 bg-primary hover:bg-primary-hover transition text-white rounded-full'
                >
                  <i className='material-icons-outlined'>add_box</i>
                  <span>&nbsp;{t('make.add')}</span>
                </button>
              }
            />
          ))}
        </ul>
      )}

      {/* Cart */}
      <div className='flex flex-col w-full items-center justify-start'>
        {editLoading && !hasItems && (
          <div className='h-[40vh] flex items-center justify-center'>
            <LoadingCart customBgColor='bg-transparent' customTextColor='text-primary-dark' />
          </div>
        )}

        {hasItems && (
          <div className='w-full py-2 text-left'>
            {/* flex-wrap (no xl:flex-nowrap): a long cart notice (e.g. Shopify's
                "list from {store URL}") drops to its own full-width row instead of
                being squeezed into a mid-sentence line break. */}
            <div className='flex flex-wrap m-4 justify-between items-center gap-2 gap-x-10'>
              <div className='flex flex-wrap w-full lg:min-w-fit xl:w-auto md:flex-nowrap justify-center items-center gap-2'>
                <input
                  className='w-full h-[44px] md:max-w-[400px] md:min-w-[320px] py-2 px-3 rounded-md text-primary-dark text-base font-secondary font-normal border-2 border-gray-200 focus:border-primary hover:cursor-text hover:bg-gray-200 transition'
                  type='text'
                  value={title}
                  placeholder={t('make.please_give_your_list_a_name')}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    setDirty(true);
                  }}
                />
                <div className='flex flex-col sm:flex-row w-full gap-2'>
                  <button
                    type='button'
                    onClick={handleSave}
                    className='min-w-max sm:w-full md:max-w-[200px] py-2 px-4 border-2 border-primary font-bold font-secondary text-center bg-primary hover:bg-primary-hover hover:border-primary-hover hover:text-offWhite text-white rounded-full transition'
                  >
                    {t('common.save_list')}
                  </button>
                  {selectedItems.size > 0 && (
                    <button
                      type='button'
                      onClick={() => setPendingDelete('bulk')}
                      className='w-full md:min-w-[210px] lg:max-w-[250px] py-2 px-4 border-2 border-red-500 bg-white hover:bg-red-200 text-red-500 font-bold font-secondary text-center rounded-full transition'
                    >
                      {t('make.remove_selected')} [{selectedItems.size}]
                    </button>
                  )}
                </div>
              </div>

              {savedCartId && (
                <CartNotice
                  cartId={savedCartId}
                  vendor={vendorId}
                  vendorDisplayName={
                    vendorMeta?.vendorDisplayName ?? editQuery.data?.vendorDisplayName
                  }
                  itemCount={items.length}
                  timestamp={editQuery.data?.timestamp}
                  store={editQuery.data?.store}
                  displaySourceSite={
                    // displaySourceSite is a field POINTER (e.g. "ap"), not the URL —
                    // dereference it against the cart like GetReview/ReviewCart do.
                    editQuery.data && editQuery.data.displaySourceSite
                      ? (editQuery.data[
                          editQuery.data.displaySourceSite as keyof CartGetData
                        ] as string)
                      : undefined
                  }
                />
              )}
            </div>

            {showMissingDetails && (
              <div className='text-center px-4 sm:px-16 py-4'>
                <div className='text-blue-700 font-primary font-normal text-left p-4 border-2 border-blue-700 rounded-lg bg-indigo-50 mb-4'>
                  <p className='py-2'>
                    <img
                      alt='warning sign'
                      src='https://assets.share-a-cart.com/images/next/info1.svg'
                      className='w-8 inline-block align-middle'
                    />
                    <span className='px-1 text-lg align-middle'>
                      {t('common.just_a_heads_up')}
                    </span>
                  </p>
                  <div
                    className='text-[#484f57] w-full'
                    dangerouslySetInnerHTML={{ __html: t('common.missing_details') }}
                  />
                </div>
              </div>
            )}

            <ul className='flex w-full flex-col items-center justify-center'>
              {items.map((item, i) => (
                <CartItem
                  key={`c-${i}-${item.asin}`}
                  item={makeItemToCartGetItem(item, locale)}
                  ccy={cartCcy}
                  ccys={cartCcyS}
                  vendor={vendorId}
                  compare={false}
                  locale={locale}
                  page='make'
                  selected={selectedItems.has(i)}
                  onToggleSelect={() => setSelectedItems((s) => toggle(s, i))}
                  onQuantityChange={(q) => handleQuantityChange(i, q)}
                  qtyErrorMessage={t('make.qty_must_be_any_num_1_999')}
                  action={
                    <button
                      type='button'
                      onClick={() => setPendingDelete(i)}
                      className='w-full min-w-fit max-w-[280px] mx-auto flex justify-center items-center font-secondary font-bold text-center h-12 py-2 px-4 border-2 border-red-500 bg-white hover:bg-red-200 text-red-500 rounded-full transition'
                    >
                      <i className='material-icons-outlined'>indeterminate_check_box</i>
                      <span>&nbsp;{t('make.remove')}</span>
                    </button>
                  }
                />
              ))}
            </ul>

            {savedCartId && (
              <div>
                <div className='m-4'>
                  <input
                    type='password'
                    value={password}
                    placeholder={t('make.enter_password_to_protect_cart_from_editing')}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setDirty(true);
                    }}
                    className='w-full h-[44px] py-2 px-3 rounded-md text-primary-dark text-base font-secondary font-normal border-2 border-gray-200 focus:border-primary hover:cursor-text hover:bg-gray-200 transition'
                  />
                </div>
                <div className='m-4 relative'>
                  <textarea
                    rows={3}
                    maxLength={1000}
                    value={note}
                    placeholder={t('make.enter_cart_note')}
                    onChange={(e) => {
                      setNote(e.target.value);
                      setDirty(true);
                    }}
                    className='w-full py-2 px-3 rounded-md text-primary-dark text-base font-secondary font-normal border-2 border-gray-200 focus:border-primary hover:cursor-text hover:bg-gray-200 transition'
                  />
                  {note.length > 0 && (
                    <p className='absolute -bottom-3.5 right-1 text-sm opacity-60'>
                      {t('make.characters_remaining')}&nbsp;{1000 - note.length}
                    </p>
                  )}
                </div>
                <div className='w-full h-0.5 bg-gray-200' />
              </div>
            )}
          </div>
        )}

        <div className='flex flex-row flex-wrap medLg:flex-nowrap gap-4 py-6 px-4 sm:px-8 md:px-14 items-center sm:justify-between w-full'>
          {!hasItems && !editLoading && !searchActive ? (
            <div className='w-full min-h-[calc(100vh-300px)] flex items-start justify-center pt-6'>
              <p className='text-center text-blueGray font-secondary font-normal'>
                {t('make.search_for_items_to_get_started')}
              </p>
            </div>
          ) : (
            savedCartId && (
              <ListActions
                page='make'
                cartId={savedCartId}
                isLoading={false}
                onDuplicate={handleDuplicate}
                onExport={handleExport}
              />
            )
          )}

          {hasItems && (
            <div className='ml-auto shrink-0 min-w-fit items-center font-secondary xs:text-lg sm:font-normal lg:text-xl text-right'>
              <p className='font-bold'>
                <span className='font-normal'>{t('common.cart_total')}&nbsp;</span>
                <span className='pl-2'>{totalLabel}</span>
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <SaveSuccessModal
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        cartId={savedCartId || ''}
        title={title}
      />

      {savedCartId && (
        <EmbedModal cartId={savedCartId} show={showEmbed} setShow={setShowEmbed} />
      )}

      {editQuery.data && <BlockedRetailerModal cart={editQuery.data} />}

      <ConfirmDeleteModal
        open={pendingDelete != null}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        bulk={pendingDelete === 'bulk'}
      />

      <MakeChat
        onAddItems={addItemsFromChat}
        onSearch={handleChatSearch}
        vendor={vendorId}
        cartCount={items.reduce((n, it) => n + it.quantity, 0)}
        onSave={handleSave}
        getCartItems={() => items}
        onRemoveIndexes={removeCartIndexes}
        onSetQuantity={setCartQuantity}
        onClearCart={clearCart}
      />
    </>
  );
};

// Small local confirm modal for item removal (uses the shared ConfirmActionModal).
const ConfirmDeleteModal = ({
  open,
  onClose,
  onConfirm,
  bulk,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  bulk: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <ConfirmActionModal
      show={open}
      setShow={(v) => {
        if (!v) onClose();
      }}
      variant='danger'
      titleText={t('popup.confirm_removal')}
      primaryBtnText={bulk ? t('popup.remove_items') : t('popup.remove_item')}
      bodyText={
        bulk
          ? t('popup.are_you_sure_you_want_to_remove_the_selected_items_from_the_list')
          : t('popup.are_you_sure_you_want_to_remove_the_selected_item_from_the_list')
      }
      onClickPrimary={onConfirm}
    />
  );
};
